import { MvxFactorId, MvxFactorStates, MvxPatternId } from './MetabolicVulnerability';

/**
 * Darren Schmidt, DC's "7-Step Blueprint to Optimal Health" — the general
 * clinical framework MVX Plus tracks against. This module holds the canonical
 * 7 steps (titles + sequence) and the marker→step routing: which steps a
 * patient's MVX factor states point to, and which markers drive each.
 *
 * Kept to step NAMES + FOCUS only — no product/dosing protocols (the marker→step
 * mapping is medium-confidence from one interview, and the webinar vs interview
 * brand sets disagree; products need a brand decision + the paid book first).
 * See the project memory `project_mvx_7step_blueprint` and [[project_mvx_poc]].
 *
 * Pure + unit-tested; the panel is presentational.
 */

export interface BlueprintStepConfig {
  /** 1..7 — sequence matters (drainage before detox; extracellular before intra). */
  num: number;
  title: string;
  /** One-line "what you do" — client-facing, non-diagnostic. */
  focus: string;
  /**
   * Driver labels (arrow + marker) when this step is indicated by the states;
   * empty array = not indicated for this patient.
   */
  drivers: (s: MvxFactorStates) => string[];
}

const BCAA: MvxFactorId[] = ['valine', 'leucine', 'isoleucine'];
const anyBcaa = (s: MvxFactorStates, state: 'high' | 'low'): boolean => BCAA.some(k => s[k] === state);

/** The 7 steps, in order, with their marker→step triggers. */
export const MVX_BLUEPRINT_STEPS: BlueprintStepConfig[] = [
  {
    num: 1,
    title: 'Foundational Foods',
    focus: 'A low-carb, meat-forward diet — remove grains and processed foods.',
    drivers: s =>
      [anyBcaa(s, 'high') ? '▲ BCAAs' : '', anyBcaa(s, 'low') ? '▼ BCAAs' : '', s.sHdlP === 'low' ? '▼ Small HDL-P' : ''].filter(Boolean),
  },
  {
    num: 2,
    title: 'Energy',
    focus: 'Rebuild cellular energy — B1/thiamine and mitochondrial support.',
    drivers: s => [s.citrate === 'high' ? '▲ Citrate' : '', anyBcaa(s, 'high') ? '▲ BCAAs' : ''].filter(Boolean),
  },
  {
    num: 3,
    title: 'Drainage',
    focus: 'Open the elimination pathways (bowel, kidney, liver, lung/skin) before detox.',
    drivers: s => [s.sHdlP === 'low' ? '▼ Small HDL-P' : ''].filter(Boolean),
  },
  {
    num: 4,
    title: 'Intestines & Immune Cleansing',
    focus: 'Clear gut parasites and organisms.',
    drivers: s => [s.glycA === 'high' ? '▲ GlycA' : ''].filter(Boolean),
  },
  {
    num: 5,
    title: 'Full-Body Immune Cleansing',
    focus: 'Systemic organism removal with antimicrobial support.',
    drivers: s => [s.glycA === 'high' ? '▲ GlycA' : ''].filter(Boolean),
  },
  {
    num: 6,
    title: 'Powerful Detoxification',
    focus: 'Binders for metals, chemicals and biotoxins.',
    drivers: s => [s.sHdlP === 'low' ? '▼ Small HDL-P' : ''].filter(Boolean),
  },
  {
    num: 7,
    title: 'Cellular Immune Cleansing',
    focus: 'Reach intracellular pathogens and toxins (including Lyme).',
    // The deepest step — indicated only when inflammation AND toxicity both persist.
    drivers: s => (s.glycA === 'high' && s.sHdlP === 'low' ? ['▲ GlycA', '▼ Small HDL-P'] : []),
  },
];

export interface BlueprintStepResult {
  num: number;
  title: string;
  focus: string;
  indicated: boolean;
  drivers: string[];
}

/** Resolve factor states to the 7 steps, marking which are indicated + their drivers. */
export function resolveBlueprintSteps(s: MvxFactorStates): BlueprintStepResult[] {
  return MVX_BLUEPRINT_STEPS.map(step => {
    const drivers = step.drivers(s);
    return { num: step.num, title: step.title, focus: step.focus, indicated: drivers.length > 0, drivers };
  });
}

/**
 * Which Blueprint steps each MVX pattern points to — for the "→ Step N" chips on
 * the pattern boxes. Optimal has no steps. (IVX story → immune/detox steps; MMX
 * story → diet/energy steps.)
 */
export const PATTERN_STEPS: Partial<Record<MvxPatternId, number[]>> = {
  [MvxPatternId.Inflammation]: [4, 5, 7],
  [MvxPatternId.ToxicBurden]: [3, 6, 7],
  [MvxPatternId.Mitochondrial]: [2],
  [MvxPatternId.CarbLoad]: [1, 2],
  [MvxPatternId.Malnutrition]: [1],
};
